import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Zap, ArrowDown, Trash2, Mail, MessageSquare, Phone, Bell, Tag as TagIcon,
  FileText, Webhook, Clock, UserPlus, FormInput, Calendar, AlertCircle, Slack,
  ListChecks, RefreshCw, Activity, CheckCircle2, ArrowLeft, Save, Play,
  ChevronRight, ChevronDown, X, Grip, Settings, Eye, History, XCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const TRIGGERS = [
  { value: "new_contact",          label: "Contact added",             icon: UserPlus,     category: "Contacts" },
  { value: "tag_added",            label: "Tag applied",               icon: TagIcon,      category: "Contacts" },
  { value: "form_submitted",       label: "Form submitted",            icon: FormInput,    category: "Contacts" },
  { value: "email_received",       label: "Email received",            icon: Mail,         category: "Inbox" },
  { value: "email_received_urgent",label: "Urgent email received",     icon: AlertCircle,  category: "Inbox" },
  { value: "email_opened",         label: "Email opened",              icon: Mail,         category: "Engagement" },
  { value: "link_clicked",         label: "Link clicked",              icon: Mail,         category: "Engagement" },
  { value: "campaign_sent",        label: "Campaign sent",             icon: Mail,         category: "Campaigns" },
  { value: "deal_won",             label: "Deal won",                  icon: Activity,     category: "Sales" },
  { value: "deal_lost",            label: "Deal lost",                 icon: Activity,     category: "Sales" },
  { value: "scheduled_time",       label: "Scheduled (time-based)",    icon: Clock,        category: "Time" },
  { value: "webhook_received",     label: "Webhook received",          icon: Webhook,      category: "Integration" },
];

const ACTIONS = [
  { value: "send_email",    label: "Send email",           icon: Mail,         category: "Email",       live: true  },
  { value: "notify_team",   label: "Notify team",          icon: Bell,         category: "Email",       live: true  },
  { value: "ai_reply",      label: "AI draft reply",       icon: Zap,          category: "AI",          live: true  },
  { value: "add_tag",       label: "Add tag",              icon: TagIcon,      category: "Contacts",    live: true  },
  { value: "remove_tag",    label: "Remove tag",           icon: TagIcon,      category: "Contacts",    live: true  },
  { value: "update_field",  label: "Update contact field", icon: FileText,     category: "Contacts",    live: true  },
  { value: "create_deal",   label: "Create deal",          icon: Activity,     category: "CRM",         live: true  },
  { value: "create_task",   label: "Create task",          icon: ListChecks,   category: "CRM",         live: true  },
  { value: "send_webhook",  label: "Send webhook",         icon: Webhook,      category: "Integration", live: true  },
  { value: "wait",          label: "Wait / Delay",         icon: Clock,        category: "Flow",        live: false },
  { value: "send_sms",      label: "Send SMS",             icon: MessageSquare,category: "Messaging",   live: false },
  { value: "make_call",     label: "Make call (AI)",       icon: Phone,        category: "Voice",       live: false },
  { value: "post_social",   label: "Post to social",       icon: RefreshCw,    category: "Social",      live: false },
  { value: "notify_slack",  label: "Notify on Slack",      icon: Slack,        category: "Internal",    live: false },
];

const RECIPES = [
  { name: "Welcome new signup",      trigger: "new_contact",         steps: [{ action: "send_email",   config: { subject: "Welcome to {{workspace}}!", body: "<h2>Hi {{first_name}},</h2><p>Welcome aboard! We're thrilled to have you.</p>" } }] },
  { name: "Tag form leads",          trigger: "form_submitted",       steps: [{ action: "add_tag",      config: { tag: "web-lead" } }] },
  { name: "Auto-reply urgent inbox", trigger: "email_received_urgent",steps: [{ action: "ai_reply",     config: {} }] },
  { name: "Create deal on signup",   trigger: "new_contact",         steps: [{ action: "create_deal",  config: { title: "New lead: {{name}}", value: 500, stage: "lead" } }] },
  { name: "Follow-up drip",          trigger: "new_contact",         steps: [{ action: "send_email",   config: { subject: "Getting started?" } }, { action: "wait", config: { delay_hours: 48 } }, { action: "add_tag", config: { tag: "drip-1" } }] },
  { name: "Win-back sequence",       trigger: "deal_lost",           steps: [{ action: "notify_team",  config: { subject: "Deal lost", body: "<p>A deal was just lost. Consider re-engagement.</p>" } }] },
];

// ─────────────────────────────────────────────
// Node Colors & Icons
// ─────────────────────────────────────────────
const NODE_COLOR = {
  trigger:      "#3B82F6",
  send_email:   "#0EA5E9",
  notify_team:  "#0EA5E9",
  ai_reply:     "#A855F7",
  ai_qualify:   "#A855F7",
  add_tag:      "#8B5CF6",
  remove_tag:   "#EC4899",
  update_field: "#F472B6",
  create_deal:  "#10B981",
  create_task:  "#10B981",
  send_webhook: "#6B7280",
  wait:         "#F59E0B",
  send_sms:     "#14B8A6",
  make_call:    "#EF4444",
  voicemail_drop:"#F97316",
  notify_slack: "#4ADE80",
  post_social:  "#818CF8",
  DEFAULT:      "#6B7280",
};

const getColor = (action) => NODE_COLOR[action] || NODE_COLOR.DEFAULT;

const getActionMeta = (value) => ACTIONS.find(a => a.value === value) || { label: value, icon: Zap };
const getTriggerMeta = (value) => TRIGGERS.find(t => t.value === value) || { label: value, icon: Zap };

function stepSummary(step) {
  const c = step.config || {};
  if (step.action === "send_email") return c.subject ? `Subject: "${c.subject.slice(0, 40)}"` : "Email not configured";
  if (step.action === "add_tag" || step.action === "remove_tag") return c.tag ? `Tag: "${c.tag}"` : "Tag not set";
  if (step.action === "wait") return c.delay_hours ? `${c.delay_hours}h delay` : "Duration not set";
  if (step.action === "create_deal") return c.title ? `"${c.title.slice(0,30)}"` : "Deal config";
  if (step.action === "send_webhook") return c.url ? c.url.slice(0, 40) : "URL not configured";
  if (step.action === "update_field") return c.field ? `${c.field} → ${c.value}` : "Field not set";
  if (step.action === "ai_reply") return "AI will generate the reply";
  if (step.action === "create_task") return c.title ? `Task: "${c.title}"` : "Task config";
  if (step.action === "notify_team") return c.subject ? `Subject: "${c.subject.slice(0,30)}"` : "Notify team";
  return "Click to configure";
}

function triggerSummary(workflow) {
  const tc = workflow.trigger_config || {};
  if (workflow.trigger === "tag_added") return tc.tag ? `Tag: "${tc.tag}"` : "Any tag applied";
  if (workflow.trigger === "scheduled_time") return tc.frequency ? `${tc.frequency} at ${tc.time || "09:00"}` : "Schedule not set";
  const meta = getTriggerMeta(workflow.trigger);
  return meta.label;
}

// ─────────────────────────────────────────────
// FlowNode component
// ─────────────────────────────────────────────
function FlowNode({ color, icon: Icon, typeLabel, title, summary, isSelected, onClick, onDelete, isNew }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: 440, cursor: "pointer", position: "relative",
        borderRadius: 14,
        border: `1.5px solid ${isSelected ? color : "var(--border)"}`,
        background: "var(--surface)",
        boxShadow: isSelected
          ? `0 0 0 3px ${color}22, 0 4px 20px ${color}18`
          : "0 2px 12px rgba(0,0,0,0.18)",
        transition: "all 0.2s",
        overflow: "hidden",
      }}
    >
      {/* Left accent bar */}
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: color, borderRadius: "14px 0 0 14px" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px 14px 20px" }}>
        {/* Icon badge */}
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={20} style={{ color }} />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color, marginBottom: 2 }}>
            {typeLabel}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--text-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {summary}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
          {isSelected && (
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, marginRight: 4 }} />
          )}
          <Settings size={14} style={{ color: "var(--text-xsoft)" }} />
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--text-xsoft)" }}
              onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
              onMouseLeave={e => e.currentTarget.style.color = "var(--text-xsoft)"}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// AddStepButton
// ─────────────────────────────────────────────
function AddStepButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 6, padding: "6px 16px",
        borderRadius: 20, border: "1.5px dashed var(--border)",
        background: "transparent", cursor: "pointer", color: "var(--text-soft)",
        fontSize: 12, fontWeight: 600, transition: "all 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-soft)"; }}
    >
      <Plus size={13} /> Add step
    </button>
  );
}

// ─────────────────────────────────────────────
// Connector line
// ─────────────────────────────────────────────
function Connector() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
      <div style={{ width: 2, height: 16, background: "var(--border)", opacity: 0.6 }} />
      <ArrowDown size={14} style={{ color: "var(--text-xsoft)", opacity: 0.5 }} />
    </div>
  );
}

// ─────────────────────────────────────────────
// StepPicker modal (choose action type)
// ─────────────────────────────────────────────
function StepPicker({ onSelect, onClose }) {
  const grouped = {};
  ACTIONS.forEach(a => { (grouped[a.category] = grouped[a.category] || []).push(a); });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} onClick={onClose} />
      <div style={{
        position: "relative", zIndex: 1, background: "var(--surface)", borderRadius: 18,
        border: "1px solid var(--border)", width: 560, maxHeight: "80vh", overflowY: "auto",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>Choose an action</div>
            <div style={{ fontSize: 12, color: "var(--text-soft)", marginTop: 2 }}>Select what happens next in the workflow</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-soft)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 16 }}>
          {Object.entries(grouped).map(([cat, actions]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-xsoft)", marginBottom: 8 }}>{cat}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {actions.map((a) => {
                  const AIcon = a.icon;
                  const color = getColor(a.value);
                  return (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => { onSelect(a.value); onClose(); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                        borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg)",
                        cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = `${color}10`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg)"; }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <AIcon size={15} style={{ color }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{a.label}</div>
                        <div style={{ fontSize: 10, color: a.live ? "var(--success)" : "#D97706" }}>{a.live ? "Live" : "Queued"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TriggerConfigPanel: config fields for the trigger node
// ─────────────────────────────────────────────
function TriggerConfigPanel({ workflow, setWorkflow, onClose }) {
  const tc = workflow.trigger_config || {};
  const update = (patch) => setWorkflow(w => ({ ...w, trigger_config: { ...w.trigger_config, ...patch } }));

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#3B82F6", marginBottom: 12 }}>Configure Trigger</div>

      <div style={{ marginBottom: 14 }}>
        <Label style={{ fontSize: 12 }}>Trigger event</Label>
        <Select value={workflow.trigger} onValueChange={(v) => setWorkflow(w => ({ ...w, trigger: v }))}>
          <SelectTrigger style={{ marginTop: 6 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {workflow.trigger === "tag_added" && (
        <div style={{ marginBottom: 14 }}>
          <Label style={{ fontSize: 12 }}>Watch for tag</Label>
          <Input placeholder="e.g. vip" value={tc.tag || ""} onChange={e => update({ tag: e.target.value })} style={{ marginTop: 6 }} />
        </div>
      )}
      {workflow.trigger === "scheduled_time" && (
        <>
          <div style={{ marginBottom: 14 }}>
            <Label style={{ fontSize: 12 }}>Frequency</Label>
            <Select value={tc.frequency || "daily"} onValueChange={(v) => update({ frequency: v })}>
              <SelectTrigger style={{ marginTop: 6 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <Label style={{ fontSize: 12 }}>Time (24h)</Label>
            <Input type="time" value={tc.time || "09:00"} onChange={e => update({ time: e.target.value })} style={{ marginTop: 6 }} />
          </div>
        </>
      )}
      {workflow.trigger === "page_visited" && (
        <div style={{ marginBottom: 14 }}>
          <Label style={{ fontSize: 12 }}>Page URL (contains)</Label>
          <Input placeholder="/pricing" value={tc.url || ""} onChange={e => update({ url: e.target.value })} style={{ marginTop: 6 }} />
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Label style={{ fontSize: 12 }}>Scenario name</Label>
        <Input value={workflow.name} onChange={e => setWorkflow(w => ({ ...w, name: e.target.value }))} style={{ marginTop: 6 }} />
      </div>
      <div style={{ marginTop: 12 }}>
        <Label style={{ fontSize: 12 }}>Description (optional)</Label>
        <Input value={workflow.description || ""} onChange={e => setWorkflow(w => ({ ...w, description: e.target.value }))} placeholder="What does this scenario do?" style={{ marginTop: 6 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// StepConfigPanel: config fields for an action step
// ─────────────────────────────────────────────
function StepConfigPanel({ step, onUpdate, onDelete, onClose }) {
  const c = step.config || {};
  const upd = (patch) => onUpdate({ ...step, config: { ...c, ...patch } });
  const meta = getActionMeta(step.action);
  const color = getColor(step.action);

  const field = (label, key, placeholder, type = "text") => (
    <div style={{ marginBottom: 14 }}>
      <Label style={{ fontSize: 12 }}>{label}</Label>
      <Input
        type={type}
        placeholder={placeholder}
        value={c[key] || ""}
        onChange={e => upd({ [key]: e.target.value })}
        style={{ marginTop: 6 }}
      />
    </div>
  );

  const textarea = (label, key, placeholder) => (
    <div style={{ marginBottom: 14 }}>
      <Label style={{ fontSize: 12 }}>{label}</Label>
      <textarea
        rows={5}
        placeholder={placeholder}
        value={c[key] || ""}
        onChange={e => upd({ [key]: e.target.value })}
        style={{
          width: "100%", marginTop: 6, padding: "8px 12px", borderRadius: 8,
          border: "1px solid var(--border)", background: "var(--bg)",
          color: "var(--text)", fontSize: 13, resize: "vertical",
        }}
      />
    </div>
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color, marginBottom: 4 }}>Configure Step</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{meta.label}</div>

      {/* Change action type */}
      <div style={{ marginBottom: 16 }}>
        <Label style={{ fontSize: 12 }}>Action type</Label>
        <Select value={step.action} onValueChange={(v) => onUpdate({ ...step, action: v, config: {} })}>
          <SelectTrigger style={{ marginTop: 6 }}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">
            {ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Action-specific fields */}
      {(step.action === "send_email" || step.action === "notify_team") && (
        <>
          {field("Subject", "subject", "Welcome to {{workspace}}!")}
          {textarea("Body (HTML)", "body", "<h2>Hi {{first_name}},</h2><p>…</p>")}
          {step.action === "send_email" && field("From name (optional)", "from_name", "Your Company")}
        </>
      )}

      {(step.action === "add_tag" || step.action === "remove_tag") && (
        field("Tag name", "tag", "e.g. vip, hot-lead")
      )}

      {step.action === "wait" && (
        <div style={{ marginBottom: 14 }}>
          <Label style={{ fontSize: 12 }}>Delay (hours)</Label>
          <Input
            type="number" min={1} placeholder="24"
            value={c.delay_hours || ""}
            onChange={e => upd({ delay_hours: parseInt(e.target.value) || 0 })}
            style={{ marginTop: 6 }}
          />
          <p style={{ fontSize: 11, color: "var(--text-soft)", marginTop: 4 }}>e.g. 24 = wait 1 day before next step</p>
        </div>
      )}

      {step.action === "create_deal" && (
        <>
          {field("Deal title", "title", "New lead: {{name}}")}
          <div style={{ marginBottom: 14 }}>
            <Label style={{ fontSize: 12 }}>Value ($)</Label>
            <Input type="number" placeholder="500" value={c.value || ""} onChange={e => upd({ value: parseFloat(e.target.value) || 0 })} style={{ marginTop: 6 }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <Label style={{ fontSize: 12 }}>Pipeline stage</Label>
            <Select value={c.stage || "lead"} onValueChange={(v) => upd({ stage: v })}>
              <SelectTrigger style={{ marginTop: 6 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["lead","qualified","proposal","negotiation","closed_won","closed_lost"].map(s => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {step.action === "send_webhook" && (
        <>
          {field("Webhook URL", "url", "https://your-server.com/hook")}
          <div style={{ marginBottom: 14 }}>
            <Label style={{ fontSize: 12 }}>Method</Label>
            <Select value={c.method || "POST"} onValueChange={(v) => upd({ method: v })}>
              <SelectTrigger style={{ marginTop: 6 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {step.action === "update_field" && (
        <>
          {field("Field name", "field", "e.g. status, company")}
          {field("New value", "value", "e.g. active, Acme Corp")}
        </>
      )}

      {step.action === "create_task" && (
        field("Task title", "title", "Follow up with {{name}}")
      )}

      {step.action === "ai_reply" && (
        <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.2)", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#A855F7", marginBottom: 4 }}>AI will auto-draft the reply</div>
          <div style={{ fontSize: 11, color: "var(--text-soft)" }}>Uses context from the incoming email. No config needed.</div>
        </div>
      )}

      {/* Delete step */}
      <button
        type="button"
        onClick={onDelete}
        style={{
          display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 14px",
          borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.06)",
          color: "#EF4444", cursor: "pointer", fontSize: 12, fontWeight: 600,
        }}
      >
        <Trash2 size={13} /> Delete this step
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// WorkflowCanvas (full-screen editor)
// ─────────────────────────────────────────────
function WorkflowCanvas({ workflow, setWorkflow, onBack, onSave, saving }) {
  const [selectedNodeIndex, setSelectedNodeIndex] = useState(null); // -1=trigger, 0..N=step
  const [showStepPicker, setShowStepPicker] = useState(false);
  const [insertAfterIndex, setInsertAfterIndex] = useState(-1);
  const [testRunning, setTestRunning] = useState(false);

  const steps = workflow.steps || [];

  const addStep = (afterIndex, actionType) => {
    const newStep = {
      id: Math.random().toString(36).slice(2),
      action: actionType,
      label: "",
      config: {},
    };
    const newSteps = [
      ...steps.slice(0, afterIndex + 1),
      newStep,
      ...steps.slice(afterIndex + 1),
    ];
    setWorkflow(w => ({ ...w, steps: newSteps }));
    setSelectedNodeIndex(afterIndex + 1);
  };

  const updateStep = (index, updated) => {
    setWorkflow(w => ({ ...w, steps: w.steps.map((s, i) => i === index ? updated : s) }));
  };

  const deleteStep = (index) => {
    setWorkflow(w => ({ ...w, steps: w.steps.filter((_, i) => i !== index) }));
    setSelectedNodeIndex(null);
  };

  const trigMeta = getTriggerMeta(workflow.trigger);
  const TrigIcon = trigMeta.icon;

  const testRun = async () => {
    if (!workflow?.id) { toast.error("Save the scenario first, then test run"); return; }
    if ((!workflow.steps || workflow.steps.length === 0) && !workflow.action) { toast.error("Add at least one action step before testing"); return; }
    setTestRunning(true);
    try {
      const { data } = await api.post(`/automations/${workflow.id}/test-run`);
      if (data.ok) toast.success(`Test run succeeded — ${data.run?.steps_total || 0} step(s) executed`);
      else toast.error(`Test run failed: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test run failed"); }
    finally { setTestRunning(false); }
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "var(--bg)" }}>
      {/* Top bar */}
      <div style={{
        height: 56, display: "flex", alignItems: "center", gap: 12, padding: "0 16px",
        borderBottom: "1px solid var(--border)", background: "var(--surface)", flexShrink: 0,
      }}>
        <button type="button" onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-soft)", display: "flex", gap: 6, alignItems: "center", fontSize: 13, padding: "4px 8px" }}>
          <ArrowLeft size={16} /> All scenarios
        </button>
        <div style={{ width: 1, height: 20, background: "var(--border)" }} />
        <input
          value={workflow.name}
          onChange={e => setWorkflow(w => ({ ...w, name: e.target.value }))}
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 15, fontWeight: 700, color: "var(--text)", maxWidth: 320 }}
          placeholder="Scenario name…"
        />
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-soft)" }}>
          <span>{workflow.is_active ? "Active" : "Paused"}</span>
          <Switch checked={!!workflow.is_active} onCheckedChange={(v) => setWorkflow(w => ({ ...w, is_active: v }))} />
        </div>
        <button
          type="button"
          onClick={testRun}
          disabled={testRunning}
          data-testid="automation-test-run-btn"
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8,
            border: "1px solid var(--border)", background: "transparent", cursor: "pointer",
            color: "var(--text-soft)", fontSize: 13,
          }}
        >
          <Play size={14} /> {testRunning ? "Running…" : "Test run"}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8,
            border: "none", background: "var(--brand)", cursor: "pointer",
            color: "#fff", fontSize: 13, fontWeight: 600,
          }}
        >
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Canvas + Config panel */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Canvas */}
        <div style={{
          flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center",
          padding: "48px 24px 80px",
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          backgroundColor: "var(--bg)",
        }}>
          {/* Trigger node */}
          <FlowNode
            color="#3B82F6"
            icon={TrigIcon}
            typeLabel="Trigger"
            title={trigMeta.label}
            summary={triggerSummary(workflow)}
            isSelected={selectedNodeIndex === -1}
            onClick={() => setSelectedNodeIndex(selectedNodeIndex === -1 ? null : -1)}
          />

          {/* Steps */}
          {steps.map((step, i) => {
            const aMeta = getActionMeta(step.action);
            const AIcon = aMeta.icon;
            const color = getColor(step.action);
            return (
              <div key={step.id || i} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Connector />
                <AddStepButton onClick={() => { setInsertAfterIndex(i - 1); setShowStepPicker(true); }} />
                <Connector />
                <FlowNode
                  color={color}
                  icon={AIcon}
                  typeLabel="Action"
                  title={aMeta.label}
                  summary={stepSummary(step)}
                  isSelected={selectedNodeIndex === i}
                  onClick={() => setSelectedNodeIndex(selectedNodeIndex === i ? null : i)}
                  onDelete={() => deleteStep(i)}
                />
              </div>
            );
          })}

          {/* Add after last */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 4 }}>
            <Connector />
            <AddStepButton onClick={() => { setInsertAfterIndex(steps.length - 1); setShowStepPicker(true); }} />
          </div>

          {/* Empty state hint */}
          {steps.length === 0 && (
            <div style={{ marginTop: 8, padding: "12px 24px", borderRadius: 10, background: "rgba(59,130,246,0.08)", border: "1px dashed rgba(59,130,246,0.3)", color: "#3B82F6", fontSize: 13, textAlign: "center" }}>
              Click &quot;+ Add step&quot; to build your workflow
            </div>
          )}
        </div>

        {/* Config panel */}
        {selectedNodeIndex !== null && (
          <div style={{
            width: 340, borderLeft: "1px solid var(--border)", background: "var(--surface)",
            overflowY: "auto", flexShrink: 0,
          }}>
            {/* Panel header */}
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                {selectedNodeIndex === -1 ? "Trigger Settings" : "Step Settings"}
              </span>
              <button type="button" onClick={() => setSelectedNodeIndex(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-soft)" }}>
                <X size={16} />
              </button>
            </div>

            {selectedNodeIndex === -1 ? (
              <TriggerConfigPanel workflow={workflow} setWorkflow={setWorkflow} onClose={() => setSelectedNodeIndex(null)} />
            ) : (
              <StepConfigPanel
                step={steps[selectedNodeIndex]}
                onUpdate={(updated) => updateStep(selectedNodeIndex, updated)}
                onDelete={() => deleteStep(selectedNodeIndex)}
                onClose={() => setSelectedNodeIndex(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* Step Picker modal */}
      {showStepPicker && (
        <StepPicker
          onSelect={(actionType) => { addStep(insertAfterIndex, actionType); }}
          onClose={() => setShowStepPicker(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Automations page
// ─────────────────────────────────────────────
export default function Automations() {
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTrigger, setNewTrigger] = useState("new_contact");
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState("workflows");

  const load = () => api.get("/automations").then(({ data }) => setItems(data));
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const loadRuns = () => {
    setRunsLoading(true);
    api.get("/automations/runs").then(({ data }) => setRuns(data)).finally(() => setRunsLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (activeTab === "history") loadRuns(); }, [activeTab]);

  const openEditor = (auto) => {
    let steps = auto.steps || [];
    if (!steps.length && auto.action) {
      steps = [{ id: Math.random().toString(36).slice(2), action: auto.action, label: "", config: auto.config || {} }];
    }
    setWorkflow({ ...auto, steps });
    setView("canvas");
  };

  const createNew = async () => {
    if (!newName.trim()) { toast.error("Name required"); return; }
    const wf = {
      name: newName.trim(),
      trigger: newTrigger,
      trigger_config: {},
      description: "",
      steps: [],
      is_active: true,
    };
    try {
      const { data } = await api.post("/automations", wf);
      setShowNew(false); setNewName(""); setNewTrigger("new_contact");
      openEditor({ ...data, steps: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const applyRecipe = async (r) => {
    const steps = (r.steps || []).map(s => ({ id: Math.random().toString(36).slice(2), label: "", ...s }));
    try {
      await api.post("/automations", { name: r.name, trigger: r.trigger, trigger_config: {}, description: r.name, steps, is_active: true });
      toast.success(`"${r.name}" added`); load();
    } catch { toast.error("Failed"); }
  };

  const saveWorkflow = async () => {
    if (!workflow) return;
    setSaving(true);
    try {
      const payload = {
        name: workflow.name,
        trigger: workflow.trigger,
        trigger_config: workflow.trigger_config || {},
        description: workflow.description || "",
        is_active: workflow.is_active,
        steps: (workflow.steps || []).map(s => ({ id: s.id, action: s.action, label: s.label || "", config: s.config || {} })),
      };
      if (workflow.id) {
        await api.put(`/automations/${workflow.id}`, payload);
      } else {
        await api.post("/automations", payload);
      }
      toast.success("Workflow saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => { await api.patch(`/automations/${id}/toggle`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this workflow?")) return; await api.delete(`/automations/${id}`); load(); };

  if (view === "canvas" && workflow) {
    return (
      <WorkflowCanvas
        workflow={workflow}
        setWorkflow={setWorkflow}
        onBack={() => { setView("list"); load(); }}
        onSave={saveWorkflow}
        saving={saving}
      />
    );
  }

  const activeCount = items.filter(a => a.is_active).length;
  const totalRuns = items.reduce((s, a) => s + (a.runs || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="automations-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Scenarios</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Make.com-style automation builder — triggers, multi-step actions, flow control, and run history.</p>
        </div>
        <Button data-testid="new-automation-btn" onClick={() => setShowNew(true)} className="gap-2 text-white" style={{ background: "var(--brand)" }}>
          <Plus className="w-4 h-4" /> New Scenario
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Active", value: activeCount, color: "var(--success)" },
          { label: "Total runs", value: totalRuns, color: "var(--brand)" },
          { label: "Scenarios", value: items.length, color: "#8B5CF6" },
        ].map(({ label, value, color }) => (
          <div key={label} className="card-premium p-4 flex items-center gap-3 rounded-xl" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div className="text-2xl font-bold" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "4px", borderRadius: 10, background: "var(--surface)", border: "1px solid var(--border)", width: "fit-content" }}>
        {[["workflows", "Scenarios"], ["recipes", "Recipes"], ["history", "Run History"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setActiveTab(v)} data-testid={`automations-tab-${v}`}
            style={{
              padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
              background: activeTab === v ? "var(--brand)" : "transparent",
              color: activeTab === v ? "#fff" : "var(--text-soft)",
            }}>
            {l} {v === "workflows" && items.length > 0 && `(${items.length})`}
          </button>
        ))}
      </div>

      {/* Workflows list */}
      {activeTab === "workflows" && (
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="card-premium p-16 text-center rounded-xl" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
              <Zap className="w-12 h-12 mx-auto mb-4 opacity-20" style={{ color: "var(--text)" }} />
              <div className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>No workflows yet</div>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>Start with a recipe or build your own visual workflow.</p>
              <Button onClick={() => setActiveTab("recipes")} className="gap-2 text-white" style={{ background: "var(--brand)" }}><Zap className="w-4 h-4" /> Browse recipes</Button>
            </div>
          ) : items.map((a, i) => {
            const trigMeta = getTriggerMeta(a.trigger);
            const TIcon = trigMeta.icon;
            const steps = a.steps || [];
            const firstStep = steps[0] || (a.action ? { action: a.action } : null);
            const fMeta = firstStep ? getActionMeta(firstStep.action) : null;
            const FIcon = fMeta?.icon || Zap;
            const stepCount = steps.length || (a.action ? 1 : 0);
            return (
              <div key={a.id} data-testid={`automation-row-${i}`}
                className="card-premium p-4 rounded-xl cursor-pointer flex items-center gap-4 hover:shadow-lg transition-all"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                onClick={() => openEditor(a)}
              >
                {/* Flow preview */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ padding: "8px 12px", borderRadius: 10, background: "rgba(59,130,246,0.1)", display: "flex", gap: 6, alignItems: "center" }}>
                    <TIcon size={15} style={{ color: "#3B82F6", flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#3B82F6", whiteSpace: "nowrap" }}>{trigMeta.label}</span>
                  </div>
                  <ArrowDown size={14} style={{ color: "var(--text-xsoft)", transform: "rotate(-90deg)", flexShrink: 0 }} />
                  {fMeta && (
                    <div style={{ padding: "8px 12px", borderRadius: 10, background: `${getColor(firstStep.action)}14`, display: "flex", gap: 6, alignItems: "center" }}>
                      <FIcon size={15} style={{ color: getColor(firstStep.action), flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: getColor(firstStep.action), whiteSpace: "nowrap" }}>{fMeta.label}</span>
                    </div>
                  )}
                  {stepCount > 1 && (
                    <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 20, background: "var(--brand-dim)", color: "var(--brand)", fontWeight: 600 }}>
                      +{stepCount - 1} more
                    </span>
                  )}
                  <div style={{ marginLeft: 8, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }}>{a.name}</div>
                    {a.description && <div style={{ fontSize: 11, color: "var(--text-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{a.description}</div>}
                  </div>
                </div>

                {/* Right side controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <span style={{ fontSize: 11, color: "var(--text-xsoft)" }}>{a.runs || 0} runs</span>
                  <Switch data-testid={`automation-toggle-${i}`} checked={!!a.is_active} onCheckedChange={() => toggle(a.id)} />
                  <Button data-testid={`automation-edit-${i}`} size="sm" variant="outline" onClick={() => openEditor(a)} className="gap-1.5" style={{ fontSize: 12 }}>
                    <Eye size={12} /> Edit
                  </Button>
                  <Button data-testid={`automation-delete-${i}`} variant="ghost" size="icon" onClick={() => remove(a.id)}>
                    <Trash2 className="w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recipes */}
      {activeTab === "recipes" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {RECIPES.map((r, i) => {
            const TIcon = getTriggerMeta(r.trigger).icon;
            const firstStep = r.steps[0];
            const AIcon = firstStep ? getActionMeta(firstStep.action).icon : Zap;
            const aColor = firstStep ? getColor(firstStep.action) : "#6B7280";
            return (
              <div key={i} data-testid={`recipe-${i}`}
                className="card-premium p-5 rounded-xl"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
                  <div style={{ padding: "5px 10px", borderRadius: 8, background: "rgba(59,130,246,0.1)", display: "flex", gap: 5, alignItems: "center" }}>
                    <TIcon size={13} style={{ color: "#3B82F6" }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#3B82F6" }}>{getTriggerMeta(r.trigger).label}</span>
                  </div>
                  <ArrowDown size={11} style={{ color: "var(--text-xsoft)", transform: "rotate(-90deg)" }} />
                  <div style={{ padding: "5px 10px", borderRadius: 8, background: `${aColor}14`, display: "flex", gap: 5, alignItems: "center" }}>
                    <AIcon size={13} style={{ color: aColor }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: aColor }}>{getActionMeta(firstStep?.action).label}</span>
                  </div>
                  {r.steps.length > 1 && <span style={{ fontSize: 10, color: "var(--text-xsoft)" }}>+{r.steps.length - 1}</span>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: "var(--text-soft)", marginBottom: 14 }}>{r.steps.length} step{r.steps.length > 1 ? "s" : ""} — ready to use</div>
                <Button data-testid={`use-recipe-${i}`} size="sm" onClick={() => applyRecipe(r)} className="w-full text-white" style={{ background: "var(--brand)" }}>
                  Use this recipe
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Run History */}
      {activeTab === "history" && (
        <div className="card-premium rounded-xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="run-history-panel">
          {runsLoading ? (
            <div className="p-16 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: "var(--brand)" }} /></div>
          ) : runs.length === 0 ? (
            <div className="p-16 text-center">
              <History className="w-12 h-12 mx-auto mb-4 opacity-20" style={{ color: "var(--text)" }} />
              <div className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>No runs yet</div>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>Every time a scenario fires (or you click &quot;Test run&quot;), it&apos;ll show up here.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {runs.map((r, i) => (
                <div key={r.id} data-testid={`run-history-row-${i}`} className="p-4 flex items-center gap-4">
                  {r.status === "success" ? (
                    <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: "var(--success)" }} />
                  ) : r.status === "running" ? (
                    <Loader2 className="w-5 h-5 shrink-0 animate-spin" style={{ color: "var(--brand)" }} />
                  ) : (
                    <XCircle className="w-5 h-5 shrink-0" style={{ color: "#EF4444" }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>{r.automation_name || "Scenario"}</div>
                    <div className="text-xs truncate" style={{ color: "var(--text-soft)" }}>
                      trigger: {r.trigger} · {r.steps_total} step{r.steps_total === 1 ? "" : "s"}
                      {r.error && <span style={{ color: "#EF4444" }}> · {r.error}</span>}
                    </div>
                  </div>
                  <div className="text-xs shrink-0" style={{ color: "var(--text-xsoft)" }}>{new Date(r.started_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New workflow modal */}
      {showNew && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} onClick={() => setShowNew(false)} />
          <div style={{
            position: "relative", zIndex: 1, background: "var(--surface)", borderRadius: 18,
            border: "1px solid var(--border)", width: 440, padding: 28,
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>New Scenario</h2>
            <p style={{ fontSize: 13, color: "var(--text-soft)", marginBottom: 20 }}>Give it a name and pick a trigger to start building.</p>
            <div style={{ marginBottom: 14 }}>
              <Label>Scenario name *</Label>
              <Input data-testid="automation-name" autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Welcome new contacts" style={{ marginTop: 6 }} onKeyDown={e => e.key === "Enter" && createNew()} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <Label>Trigger event</Label>
              <Select value={newTrigger} onValueChange={setNewTrigger}>
                <SelectTrigger data-testid="automation-trigger" style={{ marginTop: 6 }}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {TRIGGERS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button onClick={() => setShowNew(false)} variant="outline" style={{ flex: 1 }}>Cancel</Button>
              <Button data-testid="automation-save" onClick={createNew} className="text-white" style={{ flex: 1, background: "var(--brand)" }}>
                Create &amp; Open Editor
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
